/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useIntl } from 'react-intl-next';

import Badge from '@atlaskit/badge';
import { cssMap, jsx } from '@atlaskit/css';
import AssetsObjectTypeIcon from '@atlaskit/icon-lab/core/assets-object-type';
import AssetsSchemaIcon from '@atlaskit/icon-lab/core/assets-schema';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import Image from '@atlaskit/image';
import { Flex, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { type OptionType } from '@atlaskit/select';

import { ProductKeys, useProductConfig } from '../../../../../common/constants/products';
import { FilterButton } from '../../../../../common/ui/quick-find/filter-button';
import { FilterButtonIconContainer } from '../../../../../common/ui/quick-find/filter-button/filter-button-icon-container';
import { FilterPopupSelect } from '../../../../../common/ui/quick-find/filter-popup-select';
import { useSelectFilter } from '../../../../../controllers/filters/base-select-filter';
import { useFiltersDiscoverability2Enabled } from '../../../../../controllers/store';
import { filter as assetsTypeFilter } from '../../../../../controllers/store/search/filters/assets-type';

import { i18n } from './messages';

const styles = cssMap({
	imageContainer: {
		width: '16px', //  width throws a type error when using tokens
		height: '16px',
	},
});

export const AssetsTypeFilter = () => {
	const { formatMessage } = useIntl();
	const isFiltersDiscoverability2Enabled = useFiltersDiscoverability2Enabled();
	const [
		filterData,
		{ loadInitialOptions, setLookupInput, setOption, clearLookupInput, clearOptions },
	] = useSelectFilter({
		filterId: assetsTypeFilter.id,
	});

	const AssetsIcon = useProductConfig(ProductKeys.Assets).Icon;

	if (!filterData) {
		return null;
	}

	const {
		isLoading,
		suggestedOptions,
		selectedOptions,
		options,
		isLookupDefined,
		lookupInput,
		isLookupActive,
		firstSelectedOptionLabel,
		badgeLabel,
	} = filterData;

	const groupedOptions = [
		{
			label: formatMessage(i18n.assetsFilterLabelSelected),
			options: selectedOptions,
		},
		{
			label: formatMessage(i18n.assetsFilterLabelUnselected),
			options: suggestedOptions,
		},
	];

	const formatOptionLabel = (data: OptionType) => {
		return (
			<Inline alignBlock="center" space="space.100">
				<Flex>
					<Image src={data.avatarUrl} alt={data.label} css={styles.imageContainer} />
				</Flex>
				<Stack space="space.025">
					<Text>{data.label}</Text>
					<Inline alignBlock="center" space="space.100">
						<AssetsSchemaIcon label="" size="small" color="var(--ds-text-subtlest)" />
						<Text color="color.text.subtlest">{data.metadata.schemaName}</Text>
					</Inline>
				</Stack>
			</Inline>
		);
	};

	const getIconBefore = () => {
		return (
			<FilterButtonIconContainer>
				<AssetsIcon size="xxsmall" />
				<AssetsObjectTypeIcon label={formatMessage(i18n.assetsIconLabel)} size="small" />
			</FilterButtonIconContainer>
		);
	};

	const getIconAfter = () => {
		return selectedOptions.length === 0 ? (
			<ChevronDownIcon label="" size="small" />
		) : (
			badgeLabel && <Badge appearance="primary">{badgeLabel}</Badge>
		);
	};

	return (
		<FilterPopupSelect
			filterId={assetsTypeFilter.id}
			canClearFilter
			checkboxIndicators
			onDropdownOpen={loadInitialOptions}
			onDropdownClose={clearLookupInput}
			onSearchInputChange={setLookupInput}
			onSelectChange={setOption}
			options={isLookupActive ? options : groupedOptions}
			selectedOptions={selectedOptions}
			label={firstSelectedOptionLabel ? firstSelectedOptionLabel : formatMessage(i18n.assetsType)}
			placeholder={formatMessage(i18n.assetsFilterAssetsTypePlaceholder)}
			isLoading={isLoading}
			isSearchable={isLookupDefined}
			searchInput={lookupInput}
			onClearOptions={clearOptions}
			formatOptionLabel={formatOptionLabel}
			isFiltersDiscoverability2Enabled={isFiltersDiscoverability2Enabled}
			target={({ isOpen, ...targetProps }) => (
				<FilterButton
					{...targetProps}
					isSelected={!!selectedOptions.length || isOpen}
					iconBefore={getIconBefore()}
					iconAfter={getIconAfter()}
				>
					{firstSelectedOptionLabel ? firstSelectedOptionLabel : formatMessage(i18n.assetsType)}
				</FilterButton>
			)}
		/>
	);
};

export default AssetsTypeFilter;
